import React, { useEffect, useMemo } from "react";
import TargetCard from "./TargetCard";
import FloorCard from "./FloorCard";
import { P } from "../../../components/Common/Typo/typo";
import { CardTip } from "./Card";
import { CardChart } from "./Card";
import GdBg from "../../../assets/gd-bg2.svg";
import BdBg from "../../../assets/bd-bg.svg";
import {
  BarChart,
  mockDataWaste as mockDataBar,
} from "../../../components/charts/bars";
import {
  LineChart,
  mockData as mockDataLine,
} from "../../../components/charts/line";
import useSWR from "swr";
import dayjs from "dayjs";
import { formatUnixInAppTz } from "../../../utils/datetime";
import { FaArrowUp, FaArrowDown } from "react-icons/fa";

const barColor: Record<string, string> = {
  general: "#00A4C3",
  recycle: "#6DB64F",
  organic: "#F5A623",
  hazardous: "#EE7C20",
};

const wasteManagementRename = {
  general: "ทั่วไป",
  recycle: "รีไซเคิล",
  organic: "อินทรีย์",
  hazardous: "อันตราย",
};

const CHART_HEIGHT = 380
const CHART_TOP_PADDING = 40
const CHART_BOTTOM_PADDING = 80
const CHART_CONTENT_HEIGHT = CHART_HEIGHT - CHART_TOP_PADDING - CHART_BOTTOM_PADDING
const CHART_LINE_GAP = 6

const WasteManagement = () => {
  const queryParams = new URLSearchParams(location.search);
  const month = queryParams.get("month");
  const date = month
    ? dayjs(`${month.split("-")[1]}-${month.split("-")[0]}-01`)
    : dayjs();
  const [floorCardData, setFloorCardData] = React.useState<any>([]);
  const [quarterWasteCardData, setQuarterWasteCardData] = React.useState<any>(
    []
  );
  const [recycleCardData, setRecycleCardData] = React.useState<any>([]);
  const [iaqCardData, setIaqCardData] = React.useState([]);
  const [meterInterval, setMeterInterval] = React.useState<any>([]);
  const [targetCardData, setTargetCardData] = React.useState<any>([]);

  const { data: iaqData } = useSWR(["/v1/thaihealth/air_quality/dashboard/"]);
  const { data: floorData } = useSWR([
    "v1/thaihealth/floor_data/",
    {
      month: date.subtract(1, "month").format("MM-YYYY"),
      data_type: "waste",
    },
  ]);
  const { data: quarterWasteData } = useSWR([
    "v1/thaihealth/graph_data/quarterly_waste_by_type/",
    { month: date.subtract(1, "month").format("MM-YYYY") },
  ]);
  const { data: wasteInfoData } = useSWR([
    "v1/thaihealth/waste/information/",
    { month: date.subtract(1, "month").format("MM-YYYY") },
  ]);
  const { data: recycleData } = useSWR([
    "/v1/thaihealth/graph_data/recycling_management/",
    { year: date.subtract(1, "month").format("YYYY") },
  ]);
  const { data: targetData } = useSWR([
    "v1/thaihealth/target/",
    { page: "waste", date: date.subtract(1, "month").format("YYYY-MM-DD") },
  ]);

  const recycleTarget = recycleCardData[0]?.["values"]?.[0]?.["y"] ?? 0
  const recycleTargetPosition = useMemo(() => {
    if (recycleCardData.length === 0) {
      return 0;
    }
    let max =  Math.max(
      ...recycleCardData[0]['values'].map((val: any) => val.y),
      ...recycleCardData[1]['values'].map((val: any) => val.y)
    );

    max = Math.ceil(max / 5) * 5;
    return CHART_TOP_PADDING + Math.round((1 - recycleTarget / max) * (CHART_CONTENT_HEIGHT)) - CHART_LINE_GAP
  }, [recycleCardData, recycleTarget]);

  useEffect(() => {
    const data: any = [
      {
        title: "ขยะรวมทั้งอาคาร",
        value: targetData?.filter((data: any) => data.order === 1)[0].details
          .current_value,
        unit: targetData?.filter((data: any) => data.order === 1)[0].details
          .unit,
        percent: targetData?.filter((data: any) => data.order === 1)[0].percent,
        icon: "building",
        bgColor: "#6DAF5266",
        borderColor: "#6DB64F",
      },
      {
        title: "ขยะอินทรีย์",
        value: targetData?.filter((data: any) => data.order === 2)[0].details
          .current_value,
        unit: targetData?.filter((data: any) => data.order === 2)[0].details
          .unit,
        percent: targetData?.filter((data: any) => data.order === 2)[0].percent,
        icon: "fish_bone",
        bgColor: "#F5D9AA",
        borderColor: "#F5A623",
      },
      {
        title: "ขยะอันตราย",
        value: targetData?.filter((data: any) => data.order === 3)[0].details
          .current_value,
        unit: targetData?.filter((data: any) => data.order === 3)[0].details
          .unit,
        percent: targetData?.filter((data: any) => data.order === 3)[0].percent,
        icon: "skull",
        bgColor: "#9DD9E3",
        borderColor: "#00A4C3",
      },
    ];
    setTargetCardData(data);
  }, [targetData]);

  useEffect(() => {
    const data: any = [
      {
        title: "ชั้น 6",
        unit: "kg",
        value: floorData?.filter((data: any) => data.name === "6")[0].value,
        top: 10,
        left: 5,
      },
      {
        title: "ชั้น 5",
        unit: "kg",
        value: floorData?.filter((data: any) => data.name === "5")[0].value,
        top: 20,
        left: 80,
      },
      {
        title: "ชั้น 4",
        unit: "kg",
        value: floorData?.filter((data: any) => data.name === "4")[0].value,
        top: 30,
        left: 5,
      },
      {
        title: "ชั้น 3",
        unit: "kg",
        value: floorData?.filter((data: any) => data.name === "3")[0].value,
        top: 40,
        left: 80,
      },
      {
        title: "ชั้น 2",
        unit: "kg",
        value: floorData?.filter((data: any) => data.name === "2")[0].value,
        top: 50,
        left: 5,
      },
      {
        title: "ชั้น 1",
        unit: "kg",
        value: floorData?.filter((data: any) => data.name === "1")[0].value,
        top: 60,
        left: 80,
      },
      {
        title: "ชั้น B2",
        unit: "kg",
        value: floorData?.filter((data: any) => data.name === "B2")[0].value,
        top: 70,
        left: 5,
      },
      {
        title: "ชั้น B1",
        unit: "kg",
        value: floorData?.filter((data: any) => data.name === "B1")[0].value,
        top: 80,
        left: 80,
      },
    ];
    setFloorCardData(data);
  }, [floorData]);

  useEffect(() => {
    const data: any = [
      {
        title: "PM 2.5",
        value: iaqData?.pm25,
        unit: "µg/m³",
        status: iaqData?.pm25_status,
        key: "pm25",
      },
      {
        title: "CO₂",
        value: iaqData?.co2,
        unit: "ppm",
        status: iaqData?.co2_status,
        key: "co2",
      },
      {
        title: "PM 10",
        value: iaqData?.pm10,
        unit: "µg/m³",
        status: iaqData?.pm10_status,
        key: "pm10",
      },
    ];
    setIaqCardData(data);
    setMeterInterval(iaqData?.ranges);
  }, [iaqData]);

  useEffect(() => {
    setQuarterWasteCardData(quarterWasteData ? quarterWasteData : []);
  }, [quarterWasteData]);

  useEffect(() => {
    const convertRecycleDataToMockData = (data: Record<string, number>) => {
      const formattedData = Object.entries(data).map(([name, yValue]) => {
        return {
          name: name,
          values: Object.entries(yValue).map(([x, y]) => {
            const timestamp = formatUnixInAppTz(Number(x), "MMM").toLowerCase();
            return { x: timestamp, y };
          }),
        };
      });
      return formattedData;
    };

    if (recycleData) {
      const formattedRecycleData: any =
        convertRecycleDataToMockData(recycleData);
      setRecycleCardData(formattedRecycleData);
    }
  }, [recycleData]);

  return (
    <div
      className="h-full w-full relative"
      style={{
        background:
          // "radial-gradient(326.84% 138.02% at 0% 0%, #F9F9F9 0%, #ECFAFF 33%, #F4FFF0 66.5%, #FEF7EE 100%)",
          // "#ccf2ff",
          "#DCFAFF",
      }}
    >
      <div className="absolute top-0 left-0 px-8 py-4 w-full h-full">
        <div className="grid grid-cols-12 gap-2 h-full">
          <div className="col-span-3 h-full">
            <TargetCard
              targetData={targetData}
              type="waste"
              targetCardData={targetCardData}
              iaqCardData={iaqCardData}
              meterInterval={meterInterval}
            />
          </div>
          <div className="col-span-6">
            <div className="flex flex-col gap-2 h-full">
              <div className="h-[60%]">
                <FloorCard
                  card_data={floorCardData}
                  title="ขยะที่เกิดขึ้นแยกตามชั้น (Waste Generation)"
                  isWaste={true}
                  date={date}
                />
              </div>
              <div className="h-[40%] border border-[#EDEFF9] bg-white rounded-md p-[10px]">
                <div className="flex items-center justify-between">
                  <P fontSize="30px" fontWeight="700" color="#1D1D1C">
                    เปรียบเทียบขยะที่เกิดขึ้นแต่ละประเภทรายไตรมาส
                  </P>
                  <div className="px-[8px] rounded-md border border-[#DADADA] bg-[#EFEFEF]">
                    <P fontSize="20px" fontWeight="700" color="#6F6F6E">
                      Unit: กิโลกรัม, kg
                    </P>
                  </div>
                </div>
                <BarChart
                  data={Object.keys(quarterWasteCardData).reduce(
                    (acc, key) => ({
                      ...acc,
                      [wasteManagementRename[
                        key as keyof typeof wasteManagementRename
                      ] || key]: {
                        ...quarterWasteCardData[
                          key as keyof typeof quarterWasteCardData
                        ],
                        color: barColor[key],
                      },
                    }),
                    {}
                  )}
                  title=""
                  yAxisName=""
                  height="90%"
                />
              </div>
            </div>
          </div>
          <div className="col-span-3">
            <div className="flex flex-col gap-2 h-full">
              <div className="p-[10px] border border-[#EDEFF9] bg-white rounded-md">
                <p className="text-[24px] font-bold">ข้อมูลการจัดการขยะ</p>
                <div className="flex gap-2 mb-2">
                  <CardChart
                    title={`อัตราการฝังกลบขยะเดือน${date
                      .subtract(1, "month")
                      .format("MMMM")}`}
                    value={Math.abs(wasteInfoData?.msg?.landfill_rate) ?? 0}
                    unit="%"
                    description={`เปรียบเทียบกับเดือน${date
                      .subtract(2, "month")
                      .format("MMMM")}`}
                    image={wasteInfoData?.msg?.landfill_rate > 0 ? BdBg : GdBg}
                    prefix={
                      wasteInfoData?.msg?.landfill_rate < 0
                        ? "ลดลง"
                        : "เพิ่มขึ้น"
                    }
                    suffix={
                      wasteInfoData?.msg?.landfill_rate < 0 ? (
                        <FaArrowDown className="fill-[#43A452]" />
                      ) : wasteInfoData?.msg?.landfill_rate > 0 ? (
                        <FaArrowUp className="fill-[#EF4337]" />
                      ) : (
                        <></>
                      )
                    }
                    estimate={wasteInfoData?.msg?.landfill_value_diff ?? 0}
                  />
                  <CardChart
                    title={`ต้นทุนเดือน${date
                      .subtract(1, "month")
                      .format("MMMM")}`}
                    value={Math.abs(wasteInfoData?.msg?.reduced_costs) ?? 0}
                    unit="บาท"
                    description={`เปรียบเทียบกับเดือน${date
                      .subtract(2, "month")
                      .format("MMMM")}`}
                    image={wasteInfoData?.msg?.reduced_costs >= 0 ? GdBg : BdBg}
                    prefix={
                      wasteInfoData?.msg?.reduced_costs > 0
                        ? "ลดลง"
                        : "เพิ่มขึ้น"
                    }
                    suffix={
                      wasteInfoData?.msg?.reduced_costs > 0 ? (
                        <FaArrowDown className="fill-[#43A452]" />
                      ) : wasteInfoData?.msg?.reduced_costs < 0 ? (
                        <FaArrowUp className="fill-[#EF4337]" />
                      ) : (
                        <></>
                      )
                    }
                  />
                </div>
                <CardTip
                  title="การฝังกลบขยะคืออะไร?"
                  value="คือการฝังขยะลงดิน จำเป็นสำหรับขยะบางประเภท แต่มีข้อเสียคือ: ปล่อยสารเคมีอันตรายและก๊าซเรือนกระจกเมื่อขยะย่อยสลาย  ใช้พื้นที่มาก และอาจปนเปื้อนดินและน้ำใต้ดิน"
                />
              </div>

              <div className="h-full border border-[#EDEFF9] bg-white rounded-xl p-[10px]">
                <div className="flex items-center justify-between">
                  <P fontSize="24px" fontWeight="700" color="#1D1D1C">
                    อัตราขยะที่ถูกรีไซเคิลแล้ว
                  </P>
                  <div className="px-[8px] rounded-md border border-[#DADADA] bg-[#EFEFEF]">
                    <P fontSize="20px" fontWeight="700" color="#6F6F6E">
                      Unit: % ต่อขยะทั้งหมด
                    </P>
                  </div>
                </div>
                <div className="relative">
                  <LineChart
                    data={recycleCardData}
                    title=""
                    yAxisName=""
                    height={`${CHART_HEIGHT}px`}
                  />
                  <p className="absolute right-3 text-[16px]" style={{ top: recycleTargetPosition }}>
                    {recycleTarget} %
                  </p>
                </div>
                <div className="flex items-center justify-center gap-2 -mt-10">
                  <div className="flex items-center">
                    <div className="w-2.5 h-2.5 bg-green-500 mr-1"></div>
                    <P className="text-base font-bold text-gray-700">
                      อัตราขยะที่ถูกรีไซเคิล
                    </P>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2.5 h-2.5 bg-amber-500 mr-1"></div>
                    <P className="text-base font-bold text-gray-700">
                      เป้าหมาย
                    </P>
                  </div>
                </div>
                <CardTip
                  title={
                    <>
                      การรีไซเคิลคืออะไร?
                      <br />
                      คือการแปรรูปขยะให้กลับมาใช้ใหม่ ลดการใช้วัตถุดิบ
                      ประหยัดพลังงาน และลดมลพิษ โดย แปรรูปขยะให้กลับมาใช้ใหม่ได้
                      ลดการใช้ทรัพยากร ธรรมชาติ
                      ประหยัดพลังงานในการผลิตสินค้าใหม่ ลดปริมาณขยะและมลพิษ
                      <br />
                    </>
                  }
                  value={
                    <>
                      ทำไมต้องรีไซเคิล?
                      <br />
                      • รีไซเคิลกระดาษช่วยรักษาต้นไม้และประหยัดน้ำ
                      <br />
                      • รีไซเคิลพลาสติกลดการใช้น้ำมันและลดมลพิษ
                      <br />• เพิ่มงานในอุตสาหกรรมรีไซเคิล
                      ประหยัดงบประมาณในการจัดการขยะ
                    </>
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WasteManagement;
